import { STATIC_PARTY_LABELS } from './entityConfig';

export const normalizeEntities = (data) => {
  if (
    Array.isArray(data?.screening_entities) &&
    data.screening_entities.length
  ) {
    return data.screening_entities.map((entity, index) => ({
      ...entity,
      entity_key: entity?.search_id || `entity_${index}`,
      party_label:
        entity?.party_label ||
        STATIC_PARTY_LABELS[entity?.party] ||
        entity?.party ||
        'Unknown',
    }));
  }

  // Backward compatibility with old payloads
  const fallbackConfig = [
    { key: 'payer_details', defaultParty: 'payer' },
    { key: 'recipient_details', defaultParty: 'recipient' },
  ];

  return fallbackConfig
    .map(({ key, defaultParty }, index) => {
      const entity = data?.[key];
      if (!entity) return null;

      const party = entity?.party || defaultParty;
      return {
        ...entity,
        party,
        entity_key: entity?.search_id || `${defaultParty}_${index}`,
        party_label:
          entity?.party_label ||
          STATIC_PARTY_LABELS[party] ||
          party ||
          'Unknown',
      };
    })
    .filter(Boolean);
};

export const getCountKeysFromEntity = (entity = {}) =>
  Object.keys(entity).filter((key) => key.endsWith('_count'));

export const getMergedCountKeys = (entityList = []) => {
  const keySet = new Set();
  entityList.forEach((entity) => {
    getCountKeysFromEntity(entity).forEach((key) => keySet.add(key));
  });
  return [...keySet];
};

export const getEntityCount = (entity, key) => Number(entity?.[key] || 0);

export const getEntityTotalCount = (entity) =>
  getCountKeysFromEntity(entity).reduce(
    (sum, key) => sum + getEntityCount(entity, key),
    0
  );

export const getEntitySummary = (entityList = []) => {
  const totalEntities = entityList.length;
  const entitiesWithAlerts = entityList.filter(
    (entity) => getEntityTotalCount(entity) > 0
  ).length;
  const totalAlerts = entityList.reduce(
    (sum, entity) => sum + getEntityTotalCount(entity),
    0
  );

  return { totalEntities, entitiesWithAlerts, totalAlerts };
};

export const buildTransactionOptions = (entityList = []) =>
  entityList.map((entity) => ({
    label: `${entity?.name || 'Unknown'} (${entity?.party_label})`,
    value: entity?.search_id || entity?.entity_key,
    disabled: Boolean(entity?.moved_to_om || entity?.is_transaction_excluded),
  }));

export const generateDynamicTransactionResults = ({
  entities = [],
  amsList = [],
  sanctionsList = [],
  pepList = [],
  legalList = [],
  corporateList = [],
  getResultsFromList,
  isReferral = false,
}) => {
  const results = {};

  entities.forEach((entity) => {
    const searchId = entity?.search_id;
    const partyName = isReferral
      ? `${entity?.party}_details`
      : entity?.search_id;

    if (!searchId) return;

    results[partyName] = {
      ams_result: getResultsFromList(amsList, `card_${searchId}`) ?? [],
      sanction_result:
        getResultsFromList(sanctionsList, `card_${searchId}`, 'sanctions') ??
        [],
      sanction_country_result:
        getResultsFromList(
          sanctionsList,
          `card_${searchId}`,
          'sanctionsCountry'
        ) ?? [],
      mnrl_result:
        getResultsFromList(
          sanctionsList,
          `card_${searchId}`,
          'sanctionsMnrl'
        ) ?? [],
      pep_result: getResultsFromList(pepList, `card_${searchId}`) ?? [],
      legal_result: getResultsFromList(legalList, `card_${searchId}`) ?? [],
      corporate_result:
        getResultsFromList(corporateList, `card_${searchId}`) ?? [],
    };
  });

  return results;
};

export const mapDetailsToScreeningEntities = (payload = {}) => {
  const detailObjects = Object.entries(payload)
    .filter(
      ([key, value]) =>
        key.endsWith('_details') &&
        value &&
        typeof value === 'object' &&
        !Array.isArray(value)
    )
    .flatMap(([detailKey, detailValue]) =>
      Object.values(detailValue).map((item) => ({
        ...item,
        detail_source: detailKey,
      }))
    );

  const existingEntities = Array.isArray(payload?.screening_entities)
    ? payload.screening_entities
    : [];

  const combined = [...existingEntities, ...detailObjects];

  // De-duplicate by search_id (fallback to id/source/index)
  const seen = new Set();
  return combined.filter((item, index) => {
    const dedupeKey =
      item?.search_id ||
      item?.id ||
      `${item?.detail_source || 'detail'}_${index}`;
    if (seen.has(dedupeKey)) return false;
    seen.add(dedupeKey);
    return true;
  });
};
